(function () {
  if (window.__freeGiftPopupLoaded) return;
  window.__freeGiftPopupLoaded = true;

class FreeGiftPopup {
  constructor() {
    const cfg = document.querySelector('.free-gift-config');
    if (!cfg) return;

    this.shippingThreshold = parseInt(cfg.dataset.shippingThreshold || 0);
    this.currency = cfg.dataset.currency || (window.Shopify && Shopify.currency && Shopify.currency.active) || 'RON';

    try {
      this.i18n = JSON.parse(document.getElementById('free-gift-i18n')?.textContent || '{}');
    } catch (e) {
      this.i18n = {};
    }

    try {
      this.tiers = JSON.parse(cfg.dataset.tiers || '[]');
      this.tiers.forEach(t => { t.variantIds = (t.variantIds || []).map(String).filter(v => v !== ''); });
      this.tiers = this.tiers.filter(t => t.variantIds.length > 0);
      this.tiers.sort((a, b) => a.threshold - b.threshold);
    } catch (e) {
      this.tiers = [];
    }

    if (!this.tiers.length) return;

    this._lastTotal = 0;
    this.activeGiftVid = null;
    this.activeGiftKey = null;
    this.activeGiftTierIdx = -1;
    this._prevActiveVid = null;
    // True right after a chosen gift got invalidated by the total dropping below
    // its tier — lets the lower tier's "not chosen" copy read as "choose AGAIN"
    // instead of the generic first-time "choose a gift" phrasing.
    this._justDowngraded = false;
    this.busy = false;
    this._pendingCart = null;
    this._pendingSync = false;
    this._ready = false;
    this._init();
  }

  get _popups() { return [...document.querySelectorAll('.free-gift-popup')]; }
  _isCartPage() { return window.location.pathname.includes('/cart'); }
  async _init() {
    window.addEventListener('resize', () => { if (this._labelsEl) this._layoutBarLabels(this._labelsEl); }, { passive: true });
    this._watch(); this._bindPopup(); await this._sync(); this._ready = true; this._watchBar();
  }
  async _cart() { const r = await fetch('/cart.js'); return r.json(); }

  _currentTier(total) {
    let active = null;
    for (const tier of this.tiers) {
      if (total >= tier.threshold) active = tier;
    }
    return active;
  }

  _tierVids(tier) { return tier ? tier.variantIds : []; }

  _cardVid(card) {
    return card.dataset.variantId || card.querySelector('.free-gift-popup__atc')?.dataset.variantId;
  }

  _allGiftVids() { return this.tiers.flatMap(t => t.variantIds); }

  _isGiftItem(item) {
    return this._allGiftVids().includes(String(item.variant_id))
      && item.line_price === 0;
  }

  _hasGiftProperty(item) {
    return this._allGiftVids().includes(String(item.variant_id))
      && item.properties
      && String(item.properties._gift) === 'true';
  }

  _nodes(html) {
    const frag = document.createDocumentFragment();
    html.split(/(<strong>[^<]*<\/strong>)/g).forEach(part => {
      const m = part.match(/^<strong>([^<]*)<\/strong>$/);
      if (m) { const s = document.createElement('strong'); s.textContent = m[1]; frag.appendChild(s); }
      else if (part) frag.appendChild(document.createTextNode(part));
    });
    return frag;
  }

  _updateCardStates(container) {
    container.querySelectorAll('.free-gift-popup__product').forEach(card => {
      const isCurrent = String(this._cardVid(card)) === String(this.activeGiftVid);
      card.classList.toggle('is-selected', isCurrent);
      card.classList.remove('is-inactive');
    });
    container.querySelectorAll('.free-gift-popup__atc').forEach(b => {
      if (!b.dataset.originalText) b.dataset.originalText = b.textContent;
      const isCurrent = String(b.dataset.variantId) === String(this.activeGiftVid);
      b.classList.remove('is-loading');
      if (isCurrent) {
        b.disabled = false;
        b.textContent = this._t('remove_gift');
        b.dataset.isRemove = 'true';
      } else {
        b.disabled = false;
        b.textContent = b.dataset.originalText || this._t('choose_gift');
        delete b.dataset.isRemove;
      }
    });
  }

  // All gifts from every tier the customer has already unlocked — lets them freely
  // pick (or switch back to) any of them, not just the highest tier reached.
  _unlockedVids(total) {
    return this.tiers.filter(t => total >= t.threshold).flatMap(t => t.variantIds);
  }

  // Inserts a full-width label before the first visible card of each unlocked tier,
  // so a multi-tier popup doesn't read as one undifferentiated product grid.
  _labelPopupGroups(grid, unlockedTiers) {
    grid.querySelectorAll('.free-gift-popup__group-label').forEach(l => l.remove());
    if (unlockedTiers.length < 2) return;
    unlockedTiers.forEach(tier => {
      const firstCard = tier.variantIds
        .map(vid => grid.querySelector(`.free-gift-popup__product[data-variant-id="${vid}"]`))
        .find(card => card && card.style.display !== 'none');
      if (!firstCard) return;
      const label = document.createElement('div');
      label.className = 'free-gift-popup__group-label';
      label.textContent = `${tier.label} · la ${this._formatMoney(tier.threshold)}`;
      grid.insertBefore(label, firstCard);
    });
  }

  // onlyTier: when set (clicked from a specific tier group's own button), restricts
  // the popup to that tier's gifts only, instead of the full union of unlocked tiers.
  _show(changeMode = false, onlyTier = null) {
    const targetTiers = onlyTier ? [onlyTier] : this.tiers.filter(t => this._lastTotal >= t.threshold);
    const activeVids = targetTiers.flatMap(t => t.variantIds);
    if (!activeVids.length) return;

    // Same "choosing Premium replaces your current gift" notice as the drawer's
    // tier-group summary — shown here too since some customers act straight from
    // the popup without ever seeing the drawer's inline summary row.
    const isLastTier = onlyTier && onlyTier === this.tiers[this.tiers.length - 1];
    const hasLowerActiveGift = this.activeGiftVid && this.activeGiftTierIdx !== -1
      && onlyTier && this.activeGiftTierIdx < this.tiers.indexOf(onlyTier);
    const showPremiumNotice = isLastTier && this.tiers.length > 1;

    this._popups.forEach(p => {
      p.querySelectorAll('.free-gift-popup__atc').forEach(b => b.setAttribute('type', 'button'));
      p.style.display = 'flex';
      p.querySelectorAll('.free-gift-popup__product').forEach(card => {
        card.style.display = activeVids.includes(String(this._cardVid(card))) ? '' : 'none';
        card.style.cursor = 'pointer';
      });
      const grid = p.querySelector('.free-gift-popup__products');
      if (grid) this._labelPopupGroups(grid, targetTiers);

      let notice = p.querySelector('.free-gift-popup__notice');
      if (this._justDowngraded && onlyTier) {
        // Same "choose again" context as the drawer's tier-group row — this popup
        // can auto-open straight from a drawer reopen (on:cart-drawer:after-open),
        // without the customer ever seeing that row.
        const text = this._t('choose_again_suffix', { amount: this._formatMoney(onlyTier.threshold), value: this._tierValueBetweenText(onlyTier) });
        if (!notice) {
          notice = document.createElement('p');
          notice.className = 'free-gift-popup__notice';
          const subtitle = p.querySelector('.free-gift-popup__subtitle');
          if (subtitle) subtitle.insertAdjacentElement('afterend', notice);
          else p.querySelector('.free-gift-popup__box')?.appendChild(notice);
        }
        notice.textContent = text;
      } else if (showPremiumNotice) {
        const text = hasLowerActiveGift
          ? this._t('choose_premium_replace_suffix', {
              amount: this._formatMoney(onlyTier.threshold),
              value: this._tierValueBetweenText(onlyTier),
              lower_amount: this._formatMoney(this.tiers[this.activeGiftTierIdx].threshold),
            })
          : this._t('choose_premium_suffix', { amount: this._formatMoney(onlyTier.threshold), value: this._tierValueBetweenText(onlyTier) });
        if (!notice) {
          notice = document.createElement('p');
          notice.className = 'free-gift-popup__notice';
          const subtitle = p.querySelector('.free-gift-popup__subtitle');
          if (subtitle) subtitle.insertAdjacentElement('afterend', notice);
          else p.querySelector('.free-gift-popup__box')?.appendChild(notice);
        }
        notice.textContent = text;
      } else if (notice) {
        notice.remove();
      }

      this._updateCardStates(p);
      requestAnimationFrame(() => p.classList.add('is-open'));
    });
  }

  _hide() {
    this._popups.forEach(p => {
      if (!p.classList.contains('is-open')) return;
      p.classList.remove('is-open');
      p.querySelectorAll('.free-gift-popup__spinner').forEach(s => s.remove());
      p.style.display = 'none';
    });
  }

  // ===== PROGRESS BAR =====

  _injectBar(total) {
    let bar = document.querySelector('.free-gift-bar');
    const signature = `${total}|${this.activeGiftVid}`;
    // _renderBar tears down and rebuilds the entire bar (dots, labels, tier
    // groups, carousel) from scratch. on:cart-drawer:after-open always calls
    // _sync() -> _injectBar even when nothing in the cart changed, so without
    // this guard every drawer open replaced the whole DOM tree for no reason —
    // that unconditional rebuild, not label repositioning, was the visible flash.
    if (bar && this._lastRenderedSignature === signature) { return; }
    this._lastRenderedSignature = signature;
    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'free-gift-bar';
      if (this._isCartPage()) {
        const cartItems = document.querySelector('div.cart__items');
        if (!cartItems) { return; }
        cartItems.prepend(bar);
      } else {
        const drawer = document.querySelector('cart-drawer');
        if (!drawer) { return; }
        if (!drawer.hasAttribute('open')) { return; }
        const content = drawer.querySelector('.cart-drawer__content');
        if (content) { content.insertAdjacentElement('beforebegin', bar); }
        else { drawer.prepend(bar); }
      }
    }
    this._renderBar(bar, total);
  }

  _removeBar() {
    this._lastTotal = 0;
    this._lastRenderedSignature = null;
    document.querySelectorAll('.free-gift-bar').forEach(b => b.remove());
  }

  // Fills {placeholder} tokens in a translated string (locales/*.json, via
  // the Liquid-rendered data-i18n JSON) with runtime values (amounts, counts).
  _t(key, vars) {
    let text = this.i18n[key] || '';
    if (vars) {
      Object.keys(vars).forEach(k => { text = text.replace(`{${k}}`, vars[k]); });
    }
    return text;
  }

  // Formats cents using the shop's actual active currency (from data-currency,
  // set in Liquid from cart.currency.iso_code) instead of a hardcoded "lei" —
  // works correctly on multi-currency storefronts.
  _formatMoney(cents) {
    const value = Math.round(cents) / 100;
    const hasDecimals = !Number.isInteger(value);
    try {
      return new Intl.NumberFormat('ro-RO', {
        style: 'currency',
        currency: this.currency,
        minimumFractionDigits: hasDecimals ? 2 : 0,
        maximumFractionDigits: 2,
      }).format(value);
    } catch (e) {
      // Unknown/invalid currency code — fall back to a plain number + code.
      const text = hasDecimals ? value.toFixed(2) : String(value);
      return `${text} ${this.currency}`;
    }
  }

  // Parses a Shopify `money`-filtered price string (e.g. "45,00 lei") into a number.
  _parsePriceValue(text) {
    const cleaned = (text || '').replace(/[^\d.,]/g, '').trim();
    if (!cleaned) return null;
    // Strip thousands separators (a dot/comma followed by exactly 3 digits then
    // a non-digit or end), then treat whatever separator remains as decimal.
    const normalized = cleaned.replace(/[.,](?=\d{3}(\D|$))/g, '').replace(',', '.');
    const value = parseFloat(normalized);
    return Number.isNaN(value) ? null : value;
  }

  // Real min–max price range across a tier's gifts, phrased as "între X și Y lei"
  // (or "de X lei" when every gift is the same price) — used inline in a sentence.
  _tierValueBetweenText(tier) {
    const prices = tier.variantIds
      .map(vid => {
        const el = document.querySelector(`.free-gift-popup__product[data-variant-id="${vid}"] .free-gift-popup__product-price`);
        return el ? this._parsePriceValue(el.textContent) : null;
      })
      .filter(p => p !== null);
    if (!prices.length) return '';
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    return min === max
      ? this._t('value_single', { amount: this._formatMoney(Math.round(min * 100)) })
      : this._t('value_range', {
          min: this._formatMoney(Math.round(min * 100)),
          max: this._formatMoney(Math.round(max * 100)),
        });
  }

  _renderBar(bar, total) {
    const ms = [];
    if (this.shippingThreshold) {
      ms.push({ v: this.shippingThreshold, label: this._formatMoney(this.shippingThreshold), note: this._t('free_shipping_label'), type: 'shipping' });
    }
    this.tiers.forEach((tier, i) => {
      const isLast = i === this.tiers.length - 1;
      ms.push({
        v: tier.threshold,
        label: this._formatMoney(tier.threshold),
        note: tier.label,
        variantIds: tier.variantIds,
        type: 'gift',
        isPremium: isLast && this.tiers.length > 1,
      });
    });

    const max = ms[ms.length - 1].v;
    const fillPct = Math.min(100, Math.round((total / max) * 100));
    const next = ms.find(m => total < m.v);

    // ── progress track + dots ───────────────────────────────────────
    const track = document.createElement('div');
    track.className = 'free-gift-bar__track';
    const fillEl = document.createElement('div');
    fillEl.className = 'free-gift-bar__fill';
    fillEl.style.width = `${fillPct}%`;
    track.appendChild(fillEl);
    // Dots on the track; labels go in a separate row below (collision-dodged in _layoutBarLabels)
    const labelsEl = document.createElement('div');
    labelsEl.className = 'free-gift-bar__labels';
    ms.forEach((m) => {
      const pct = Math.round((m.v / max) * 100);
      const done = total >= m.v;
      const icon = done ? '✓' : '';
      const isNext = !done && next === m;
      const stateCls = [done ? 'is-done' : '', isNext ? 'is-next' : ''].filter(Boolean).join(' ');
      const dotEl = document.createElement('div');
      dotEl.className = `free-gift-bar__ms ${stateCls}`.trim();
      dotEl.style.left = `${pct}%`;

      const dotIcon = document.createElement('div');
      dotIcon.className = 'free-gift-bar__ms-dot'; dotIcon.textContent = icon;
      dotEl.appendChild(dotIcon);
      track.appendChild(dotEl);

      const labelEl = document.createElement('div');
      labelEl.className = `free-gift-bar__label ${stateCls}`.trim();
      labelEl.dataset.pct = pct;
      const nameEl = document.createElement('div');
      nameEl.className = 'free-gift-bar__ms-name';
      nameEl.textContent = m.note;
      labelEl.appendChild(nameEl);
      const thresholdEl = document.createElement('div');
      thresholdEl.className = 'free-gift-bar__ms-threshold';
      thresholdEl.textContent = this._t('milestone_threshold', { amount: m.label });
      labelEl.appendChild(thresholdEl);
      const statusEl = document.createElement('div');
      statusEl.className = 'free-gift-bar__ms-status';
      statusEl.textContent = done
        ? this._t('milestone_done')
        : this._t('milestone_remaining', { amount: this._formatMoney(m.v - total) });
      labelEl.appendChild(statusEl);
      labelsEl.appendChild(labelEl);
    });

    // ── gift picker / preview ──────────────────────────────────────
    // Every tier the customer has unlocked so far — shown together so they can
    // freely upgrade OR downgrade between any of them, not just the highest one.
    const unlockedTiers = this.tiers.filter(t => total >= t.threshold);

    // ── assemble ────────────────────────────────────────────────────
    bar.textContent = '';
    var _heroEl = document.createElement('div');
    _heroEl.className = 'free-gift-bar__hero';
    if (!next) { _heroEl.innerHTML = (this.i18n['hero_all'] || 'Ai transport gratuit și cadou!'); }
    else {
      var _amt = '<strong>' + this._formatMoney(next.v - total) + '</strong>';
      if (next.type === 'shipping') { _heroEl.innerHTML = (this.i18n['hero_shipping'] || 'Mai adaugă {amount} și primești transport gratuit').replace('{amount}', _amt); }
      else { var _lowerGift = ms.some(function(m){ return m.type === 'gift' && m.v < next.v; }); _heroEl.innerHTML = (this.i18n[_lowerGift ? 'hero_gift_upgrade' : 'hero_gift'] || (_lowerGift ? 'Mai adaugă {amount} și poți alege un cadou mai valoros' : 'Mai adaugă {amount} și alegi un cadou gratuit')).replace('{amount}', _amt); }
    }
    bar.appendChild(_heroEl);
    bar.appendChild(track);
    bar.appendChild(labelsEl);
    if (unlockedTiers.length) bar.appendChild(this._buildInlineSection(unlockedTiers));
    // Preview of EVERY not-yet-reached tier, not just the nearest one — so a
    // tier further out (e.g. Premium, while still below the first threshold)
    // stays visible and browsable, just collapsed, instead of disappearing
    // entirely until the nearer tier is unlocked.
    const upcomingTiers = this.tiers.filter(t => total < t.threshold);
    upcomingTiers.forEach((upcomingTier, idx) => {
      if (upcomingTier.threshold - total > 10000) return; // B-rafinat: ascunde preview cand pragul e la >100 lei
      const previewEl = this._buildGiftPreviewCarousel(upcomingTier, upcomingTier.threshold - total);
      // Only the NEAREST upcoming tier, and only when NO tier is unlocked yet —
      // once one is, the tier-group above becomes "the" expanded section and
      // every preview (including this one) goes back to collapsed.
      if (this._isCartPage() && !unlockedTiers.length && idx === 0) previewEl.classList.add('is-expanded');
      bar.appendChild(previewEl);
    });

    // Kept invisible until the later passes settle — otherwise the customer sees
    // the first (rough) position, then a visible jump once the 300ms/fonts-ready
    // pass corrects it (that jump was the "flash" on cart-drawer open).
    labelsEl.style.opacity = '0';
    this._labelsEl = labelsEl;
    requestAnimationFrame(() => this._layoutBarLabels(labelsEl));
    setTimeout(() => {
      this._layoutBarLabels(labelsEl);
      labelsEl.style.opacity = '1';
    }, 300);
    // Webfonts can swap in after our initial passes, shifting text width and
    // desyncing label positions from their dots — re-layout once fonts settle.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        this._layoutBarLabels(labelsEl);
        labelsEl.style.opacity = '1';
      });
    } else {
      labelsEl.style.opacity = '1';
    }
  }

  // Positions labels under their dots, then pushes overlapping neighbours apart.
  _layoutBarLabels(labelsEl) {
    if (!labelsEl.isConnected) return;
    const labels = [...labelsEl.children];
    if (!labels.length) return;
    const track = labelsEl.previousElementSibling;
    const dots = track ? [...track.querySelectorAll('.free-gift-bar__ms-dot')] : [];
    if (dots.length !== labels.length) return;
    const cw = labelsEl.clientWidth;
    if (!cw) return;
    const labelsRect = labelsEl.getBoundingClientRect();
    // Measure the real safe overhang from the DOM (bar's own padding beyond labelsEl's
    // edges) instead of guessing a fixed px — adapts automatically to any breakpoint/theme
    // padding and never lets a label escape the card, while still allowing end labels
    // (which sit right at the 0%/100% dot) enough room to center properly.
    const bar = labelsEl.parentElement;
    const barRect = bar ? bar.getBoundingClientRect() : labelsRect;
    // Leave visible breathing room from the card edge (8px), not just enough to avoid clipping.
    const OVERHANG_LEFT = Math.max(0, labelsRect.left - barRect.left - 8);
    const OVERHANG_RIGHT = Math.max(0, barRect.right - labelsRect.right - 8);
    const GAP = 4;
    const boxes = labels.map((el, i) => {
      el.style.left = '0';
      el.style.width = '';
      const w = el.offsetWidth;
      el.style.width = `${w}px`;
      // Anchor to the dot's actual rendered position, not a recomputed percentage —
      // guarantees the label sits under its real dot even if container widths differ by rounding.
      const dotRect = dots[i].getBoundingClientRect();
      const center = dotRect.left + dotRect.width / 2 - labelsRect.left;
      return { el, w, x: center - w / 2 };
    });

    // Resolve overlaps symmetrically: both neighbours give way equally,
    // so a label stays as close as possible to its own dot instead of one
    // label absorbing the entire push (which visually detaches it from its dot).
    const resolveOverlaps = () => {
      for (let pass = 0; pass < 8; pass++) {
        let moved = false;
        for (let i = 0; i < boxes.length - 1; i++) {
          const a = boxes[i], b = boxes[i + 1];
          const overlap = (a.x + a.w + GAP) - b.x;
          if (overlap > 0) {
            a.x -= overlap / 2;
            b.x += overlap / 2;
            moved = true;
          }
        }
        if (!moved) break;
      }
    };
    resolveOverlaps();

    if (boxes.length) {
      const first = boxes[0];
      if (first.x < -OVERHANG_LEFT) first.x = -OVERHANG_LEFT;
      const last = boxes[boxes.length - 1];
      const maxX = cw + OVERHANG_RIGHT - last.w;
      if (last.x > maxX) last.x = maxX;
      resolveOverlaps();
    }

    let h = 0;
    boxes.forEach(b => {
      b.el.style.left = `${Math.round(b.x)}px`;
      h = Math.max(h, b.el.offsetHeight);
    });
    labelsEl.style.minHeight = `${h}px`;
  }

  // Renders one grid group (image + name cards) for a single unlocked tier,
  // used inside _buildInlineSection when there's more than one tier to show.
  // Collapsed to a one-line row by default in BOTH states — chosen ("✓ Cadou
  // ales: X · Schimbă") or not yet chosen ("Cadou · Alege 1 din 4  ›") — so two
  // unlocked tiers never dump two full grids on screen at once. The full
  // header+grid only reappears once the row itself is clicked.
  _buildTierGroup(tier) {
    const group = document.createElement('div');
    group.className = 'free-gift-bar__tier-group';

    const isThisTierActive = tier.variantIds.includes(String(this.activeGiftVid));
    if (isThisTierActive) group.classList.add('is-chosen');
    const isLastTier = tier === this.tiers[this.tiers.length - 1];
    // Only meaningful if a LOWER-tier gift is actually chosen right now —
    // otherwise "will be removed" refers to nothing and just confuses.
    const hasLowerActiveGift = this.activeGiftVid && this.activeGiftTierIdx !== -1
      && this.activeGiftTierIdx < this.tiers.indexOf(tier);

    const summary = document.createElement('div');
    summary.className = 'free-gift-bar__tier-summary';
    summary.addEventListener('click', () => group.classList.toggle('is-expanded'));

    if (isThisTierActive) {
      const check = document.createElement('span');
      check.className = 'free-gift-bar__tier-summary-check';
      check.textContent = '✓';
      const summaryText = document.createElement('span');
      summaryText.className = 'free-gift-bar__tier-summary-text';
      summaryText.textContent = this._t('chosen_label');
      const summaryName = document.createElement('strong');
      const chosenTitleEl = document.querySelector(`.free-gift-popup__product[data-variant-id="${this.activeGiftVid}"] .free-gift-popup__product-title`);
      summaryName.textContent = chosenTitleEl ? chosenTitleEl.textContent : '';
      summaryText.appendChild(summaryName);
      const summaryToggle = document.createElement('span');
      summaryToggle.className = 'free-gift-bar__tier-summary-toggle';
      summaryToggle.textContent = this._t('change');
      summary.appendChild(check);
      summary.appendChild(summaryText);
      summary.appendChild(summaryToggle);
    } else {
      group.classList.add('is-available');
      const icon = document.createElement('span');
      icon.className = 'free-gift-bar__tier-summary-icon';
      icon.innerHTML = '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 12 20 22 4 22 4 12"></polyline><rect x="2" y="7" width="20" height="5"></rect><line x1="12" y1="22" x2="12" y2="7"></line><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"></path><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"></path></svg>';
      const summaryText = document.createElement('span');
      summaryText.className = 'free-gift-bar__tier-summary-text';
      if (this._justDowngraded) {
        // The gift they'd already chosen just got invalidated by a total drop —
        // "choose again" reads more accurately here than the generic first-time copy.
        summaryText.textContent = this._t('choose_again_suffix', { amount: this._formatMoney(tier.threshold), value: this._tierValueBetweenText(tier) });
      } else {
        summaryText.append(`${this._t('choose_prefix')} `);
        const oneStrong = document.createElement('strong');
        oneStrong.textContent = '1';
        summaryText.appendChild(oneStrong);
        // No "will be removed" mention here — the collapsed row stays short;
        // the full replace-aware text already shows in the header once expanded.
        let suffixText;
        if (isLastTier && this.tiers.length > 1) {
          // Short variant, no threshold — the collapsed row sits right under the
          // milestone bar which already says "la {amount}", repeating it here is redundant.
          suffixText = this._t('choose_premium_suffix_short', { value: this._tierValueBetweenText(tier) });
        } else {
          suffixText = this._t('choose_suffix', { label: tier.label, value: this._tierValueBetweenText(tier) });
        }
        summaryText.append(` ${suffixText}`);
      }
      const chevron = document.createElement('span');
      chevron.className = 'free-gift-bar__tier-summary-chevron';
      chevron.textContent = '›';
      summary.appendChild(icon);
      summary.appendChild(summaryText);
      summary.appendChild(chevron);
    }
    group.appendChild(summary);

    const header = document.createElement('div');
    header.className = 'free-gift-bar__inline-header';
    const titleWrap = document.createElement('div');
    // Not-yet-chosen tier already shows "{label} · Alege 1 din N" in the summary
    // row above (visible even when expanded) — repeating it here would be redundant.
    if (isThisTierActive) {
      const strong = document.createElement('strong');
      strong.textContent = this._t('inline_title', { label: tier.label, count: tier.variantIds.length });
      titleWrap.appendChild(strong);
    }
    const sub = document.createElement('span');
    if (!isThisTierActive && isLastTier && this.tiers.length > 1) {
      // Same Premium messaging as the summary row above — repeated here since
      // this line is the only one visible once the row is expanded but the
      // summary row's own text has scrolled out of view on a long product grid.
      sub.textContent = hasLowerActiveGift
        ? this._t('choose_premium_replace_suffix', {
            amount: this._formatMoney(tier.threshold),
            value: this._tierValueBetweenText(tier),
            lower_amount: this._formatMoney(this.tiers[this.activeGiftTierIdx].threshold),
          })
        : this._t('choose_premium_suffix', { amount: this._formatMoney(tier.threshold), value: this._tierValueBetweenText(tier) });
    } else {
      sub.textContent = this._t('inline_sub', { amount: this._formatMoney(tier.threshold) });
    }
    titleWrap.appendChild(sub);
    header.appendChild(titleWrap);
    group.appendChild(header);

    const grid = document.createElement('div');
    grid.className = 'free-gift-bar__inline-grid';
    tier.variantIds.forEach(vid => {
      const srcImg = document.querySelector(`.free-gift-popup__product[data-variant-id="${vid}"] img`);
      const titleEl = document.querySelector(`.free-gift-popup__product[data-variant-id="${vid}"] .free-gift-popup__product-title`);
      if (!srcImg) return;
      const card = document.createElement('div');
      card.className = 'free-gift-bar__mini-card' + (String(this.activeGiftVid) === String(vid) ? ' is-selected' : '');
      const img = document.createElement('img');
      img.src = srcImg.src; img.alt = srcImg.alt || ''; img.loading = 'lazy';
      card.appendChild(img);
      if (titleEl) {
        const span = document.createElement('span');
        span.textContent = titleEl.textContent;
        card.appendChild(span);
      }
      card.addEventListener('click', async () => {
        if (this.busy || card.classList.contains('is-loading')) return;
        const isSelected = String(this.activeGiftVid) === String(vid);
        card.classList.add('is-loading');
        try {
          if (isSelected) await this._removeActiveGiftAndClose();
          else await this._add(vid);
        } finally {
          card.classList.remove('is-loading');
        }
      });
      grid.appendChild(card);
    });
    group.appendChild(grid);

    // Bottom CTA — mirrors the top summary row's action, so the button under
    // the grid is symmetric whether or not this tier's gift is chosen yet.
    const cta = document.createElement('button');
    cta.type = 'button';
    cta.className = 'free-gift-bar__inline-cta';
    if (isThisTierActive) {
      cta.textContent = this._t('cta_change');
      cta.addEventListener('click', () => this._show(true, tier));
    } else {
      cta.textContent = this._t('cta_choose');
      cta.addEventListener('click', () => this._show(false, tier));
    }
    group.appendChild(cta);

    return group;
  }

  // One or more unlocked tiers, each as its own labelled group, so the customer
  // can freely upgrade or downgrade between any tier they've already reached.
  _buildInlineSection(tiers) {
    const el = document.createElement('div');
    el.className = 'free-gift-bar__inline-section';

    const footer = document.createElement('p');
    footer.className = 'free-gift-bar__inline-footer';
    footer.textContent = this._t('inline_footer');
    el.appendChild(footer);

    tiers.forEach((tier, idx) => {
      const group = this._buildTierGroup(tier);
      // On the full /cart page there's room to guide attention without the drawer's
      // space crunch — auto-expand only the highest (most recently reached) tier,
      // so the customer sees its gifts immediately without hiding the others entirely.
      if (this._isCartPage() && idx === tiers.length - 1) group.classList.add('is-expanded');
      el.appendChild(group);
    });

    return el;
  }

  // Preview of the NEXT (not-yet-reached) gift tier: a simple equal-size grid of
  // all its gifts (image + name), no selection UI — this is a pure visual preview,
  // nothing here adds to cart or opens the popup.
  _buildGiftPreviewCarousel(tier, remainingCents) {
    const el = document.createElement('div');
    el.className = 'free-gift-preview';

    const header = document.createElement('div');
    header.className = 'free-gift-preview__header';

    const icon = document.createElement('span');
    icon.className = 'free-gift-preview__icon';
    icon.innerHTML = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 12 20 22 4 22 4 12"></polyline><rect x="2" y="7" width="20" height="5"></rect><line x1="12" y1="22" x2="12" y2="7"></line><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"></path><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"></path></svg>';

    const titleWrap = document.createElement('div');
    titleWrap.className = 'free-gift-preview__title-wrap';
    const strong = document.createElement('strong');
    strong.textContent = this._t('preview_title', { value: this._tierValueBetweenText(tier) });
    const sub = document.createElement('p');
    sub.textContent = this._t('preview_sub', { amount: this._formatMoney(tier.threshold) });
    titleWrap.appendChild(strong);
    titleWrap.appendChild(sub);

    const remainingWrap = document.createElement('div');
    remainingWrap.className = 'free-gift-preview__remaining';
    const remainingStrong = document.createElement('strong');
    remainingStrong.textContent = this._t('remaining_amount', { amount: this._formatMoney(remainingCents) });
    const remainingSpan = document.createElement('span');
    remainingSpan.textContent = this._t('remaining_label');
    remainingWrap.appendChild(remainingStrong);
    remainingWrap.appendChild(remainingSpan);

    const chevron = document.createElement('span');
    chevron.className = 'free-gift-preview__chevron';
    chevron.textContent = '›';

    header.appendChild(icon);
    header.appendChild(titleWrap);
    header.appendChild(remainingWrap);
    header.appendChild(chevron);
    el.appendChild(header);

    // Collapsed by default — the header alone (icon, count, remaining amount) already
    // communicates the offer; the image grid only takes space once the customer asks for it.
    header.addEventListener('click', () => el.classList.toggle('is-expanded'));

    // Gather renderable gift data once, then paginate into pages of 4 so the
    // grid stays uniform-sized regardless of how many gifts this tier has.
    const gifts = tier.variantIds
      .map(vid => {
        const srcImg = document.querySelector(`.free-gift-popup__product[data-variant-id="${vid}"] img`);
        const titleEl = document.querySelector(`.free-gift-popup__product[data-variant-id="${vid}"] .free-gift-popup__product-title`);
        return srcImg ? { src: srcImg.src, alt: srcImg.alt || '', name: titleEl ? titleEl.textContent : '' } : null;
      })
      .filter(Boolean);

    const PAGE_SIZE = 4;
    const pages = [];
    for (let i = 0; i < gifts.length; i += PAGE_SIZE) pages.push(gifts.slice(i, i + PAGE_SIZE));

    const grid = document.createElement('div');
    grid.className = 'free-gift-preview__grid';
    el.appendChild(grid);

    let dotsEl = null;
    let currentPage = 0;

    const renderPage = (idx) => {
      currentPage = idx;
      grid.textContent = '';
      pages[idx].forEach(gift => {
        const item = document.createElement('div');
        item.className = 'free-gift-preview__item';
        const img = document.createElement('img');
        img.src = gift.src;
        img.alt = gift.alt;
        img.loading = 'lazy';
        item.appendChild(img);
        if (gift.name) {
          const name = document.createElement('span');
          name.textContent = gift.name;
          item.appendChild(name);
        }
        grid.appendChild(item);
      });
      if (dotsEl) {
        [...dotsEl.children].forEach((d, i) => d.classList.toggle('is-active', i === idx));
      }
    };

    renderPage(0);

    if (pages.length > 1) {
      dotsEl = document.createElement('div');
      dotsEl.className = 'free-gift-preview__dots';
      pages.forEach((_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'free-gift-preview__dot' + (i === 0 ? ' is-active' : '');
        dot.setAttribute('aria-label', this._t('page_label', { n: i + 1 }));
        dot.addEventListener('click', () => renderPage(i));
        dotsEl.appendChild(dot);
      });
      el.appendChild(dotsEl);

      // Swipe support (mobile): a horizontal drag past a small threshold moves one page.
      let touchStartX = null;
      grid.addEventListener('touchstart', (e) => { touchStartX = e.touches[0].clientX; }, { passive: true });
      grid.addEventListener('touchend', (e) => {
        if (touchStartX === null) return;
        const dx = e.changedTouches[0].clientX - touchStartX;
        touchStartX = null;
        if (Math.abs(dx) < 40) return;
        if (dx < 0 && currentPage < pages.length - 1) renderPage(currentPage + 1);
        else if (dx > 0 && currentPage > 0) renderPage(currentPage - 1);
      }, { passive: true });
    }

    return el;
  }

  // ===== CART OPERATIONS =====

  // Retries a few times before giving up — a single transient network failure
  // (real-world mobile connections, e.g. the "Load failed" issue found earlier)
  // must not leave an invalidated gift sitting in the cart at a real price.
  // Throwing on final failure (instead of swallowing it) keeps activeGiftVid /
  // activeGiftTierIdx pointing at the stale gift, so the NEXT cart sync retries
  // removal automatically — callers must not clear that tracking state unless
  // this resolves successfully.
  async _removeGift(lineKey, attempts = 3) {
    for (let attempt = 1; attempt <= attempts; attempt++) {
      try {
        const resp = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: lineKey, quantity: 0 }),
        });
        if (resp.ok) return;
      } catch (e) {
        // network failure — fall through to retry
      }
      if (attempt < attempts) await new Promise(r => setTimeout(r, 400 * attempt));
    }
    throw new Error(`[gift] failed to remove line ${lineKey} after ${attempts} attempts`);
  }

  // Locks every gift-widget button/card (bar + popup) for the duration of the
  // request, so a second click can't fire while the first is still in flight.
  // Deliberately leaves the real cart line items (cart-items) untouched — the
  // customer can still edit quantities/remove products while the gift request runs.
  _setCartLoading(on) {
    document.body.classList.toggle('free-gift-busy', on);
  }

  async _refreshCartItems() {
    const cart = await this._cart();
    const refreshes = [];
    document.querySelectorAll('cart-items').forEach(el => {
      el.currentTotalItemCount = cart.item_count;
      if (typeof el.refresh === 'function') refreshes.push(el.refresh());
    });
    await Promise.allSettled(refreshes);
    return cart;
  }

  async _handleCart(cart) {
    const total = cart.items
      .filter(i => !this._hasGiftProperty(i))
      .reduce((sum, i) => sum + i.line_price, 0);
    const prevTier = this._currentTier(this._lastTotal);
    this._lastTotal = total;
    const tier = this._currentTier(total);

    const tierIdx = tier ? this.tiers.indexOf(tier) : -1;
    const prevTierIdx = prevTier ? this.tiers.indexOf(prevTier) : -1;
    const tierIncreased = !!prevTier && tierIdx > prevTierIdx;
    if (tierIncreased) localStorage.removeItem('giftPopupShown');
    if (this._ready && tier && (tierIncreased || !this.activeGiftVid) && !localStorage.getItem('giftPopupShown')) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer?.hasAttribute('open')) {
        localStorage.setItem('giftPopupShown', '1');
        // Auto-popup announces the milestone just reached — show only that tier's
        // gifts, not the full unlocked union (that union still lives in the inline
        // picker under the bar, where upgrade/downgrade between tiers stays possible).
        this._show(false, tier);
      }
    }

    if (!tier) {
      this._hide();
      document.querySelectorAll('.free-gift-change-btn').forEach(b => b.remove());
      this.activeGiftVid = null;
      this.activeGiftKey = null;
      this.activeGiftTierIdx = -1;
      localStorage.removeItem('giftPopupShown');
      if (total > 0) this._injectBar(total);
      else { this._removeBar(); this._hideCartBanner(); }
      const anyGift = cart.items.find(i => this._hasGiftProperty(i));
      if (anyGift && !this.busy) {
        this.busy = true;
        this._setCartLoading(true);
        let bannerMsg = null;
        try {
          await this._removeGift(anyGift.key);
          const nextTier = this.tiers.find(t => t.threshold > total);
          const limit = nextTier ? this._formatMoney(nextTier.threshold) : '';
          if (total > 0) bannerMsg = this._t('banner_gift_removed', { amount: limit });
          await this._refreshCartItems();
        } finally { this.busy = false; this._setCartLoading(false); }
        this._pendingSync = false;
        if (bannerMsg) this._showCartBanner(bannerMsg);
      }
      if (this._pendingSync) {
        this._pendingSync = false;
        await this._sync();
      }
      return;
    }

    this._hideCartBanner();

    const rightVids = this._tierVids(tier);
    const currentTierIndex = this.tiers.indexOf(tier);

    // Derived purely from the cart item itself (properties._gift, persisted
    // server-side by Shopify) and which tier its variant belongs to — NOT from
    // this.activeGiftTierIdx/activeGiftVid. Those reset to -1/null on every fresh
    // page load, so a check that depended on them would silently stop detecting
    // an invalidated gift the moment the customer opens a new tab or reloads —
    // exactly when a prior removal attempt may have failed (bad connection) and
    // most needs retrying. line_price is irrelevant here too: whether an external
    // discount has already reverted the item to a real price or not, a gift
    // variant tagged _gift=true that belongs to a tier above the current one is
    // always wrong and must go.
    const wrongGift = cart.items.find(i => {
      if (!this._hasGiftProperty(i)) return false;
      const giftTierIdx = this.tiers.findIndex(t => t.variantIds.includes(String(i.variant_id)));
      return giftTierIdx > currentTierIndex;
    });

    if (wrongGift && !this.busy) {
      const giftEl = document.querySelector(`cart-items [data-variant-id="${wrongGift.variant_id}"]`);
      const giftRow = giftEl?.closest('tr.cart-item, li.cart-item, .cart-item, tr, li') || giftEl;
      if (giftRow) {
        giftRow.style.transition = 'opacity 0.2s ease';
        giftRow.style.opacity = '0';
      }
      this.busy = true;
      this._setCartLoading(true);
      try {
        await this._removeGift(wrongGift.key);
        await this._refreshCartItems();
        const nextTier = this.tiers.find(t => t.threshold > total);
        const limit = nextTier ? this._formatMoney(nextTier.threshold) : '';
        // `tier` here is still the highest tier the current (lowered) total still
        // qualifies for — e.g. the 300 tier when premium (600) was just lost.
        if (total > 0) this._showCartBanner(this._t('banner_premium_unavailable', { amount: limit, lower_amount: this._formatMoney(tier.threshold) }));
      } finally { this.busy = false; this._setCartLoading(false); }
      this.activeGiftVid = null;
      this.activeGiftKey = null;
      this.activeGiftTierIdx = -1;
      this._justDowngraded = true;
      localStorage.removeItem('giftPopupShown');
    }

    document.querySelectorAll('.free-gift-change-btn').forEach(b => b.remove());

    const gift = cart.items.find(i =>
      this._isGiftItem(i) && rightVids.includes(String(i.variant_id)) &&
      !(wrongGift && i.key === wrongGift.key)
    ) || null;
    const anyGiftInCart = cart.items.find(i =>
      this._hasGiftProperty(i) &&
      !(wrongGift && i.key === wrongGift.key)
    ) || null;

    if (gift) {
      this.activeGiftVid = String(gift.variant_id);
      this.activeGiftKey = gift.key;
      if (this.activeGiftTierIdx === -1) {
        this.activeGiftTierIdx = this.tiers.findIndex(t => t.variantIds.includes(String(gift.variant_id)));
      }
      this._injectBar(total);
      requestAnimationFrame(() => this._injectGiftLabel(gift.variant_id));
    } else {
      this.activeGiftVid = anyGiftInCart ? String(anyGiftInCart.variant_id) : null;
      this.activeGiftKey = anyGiftInCart ? anyGiftInCart.key : null;
      if (anyGiftInCart && this.activeGiftTierIdx === -1) {
        this.activeGiftTierIdx = this.tiers.findIndex(t => t.variantIds.includes(String(anyGiftInCart.variant_id)));
      }
      this._injectBar(total);
    }
  }

  async _sync() {
    const cart = await this._cart();
    await this._handleCart(cart);
  }

  async _add(variantId) {
    if (this.busy) return;
    this.busy = true;
    this._setCartLoading(true);

    try {
      const currentCart = await this._cart();

      const existingGifts = currentCart.items.filter(i => this._hasGiftProperty(i));
      for (const g of existingGifts) await this._removeGift(g.key);

      const resp = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1, properties: { _gift: 'true' } }),
      });
      if (!resp.ok) throw new Error('cart/add.js failed');
      const addedItem = await resp.json();

      this.activeGiftVid = String(variantId);
      this.activeGiftKey = addedItem.key;
      this._justDowngraded = false;
      // Tier the CHOSEN gift belongs to — not the highest tier currently qualified for.
      // Picking a lower-tier gift while a higher tier is also unlocked must not get
      // treated as a "too-high-tier" gift once the total later drops back down.
      this.activeGiftTierIdx = this.tiers.findIndex(t => t.variantIds.includes(String(variantId)));

      this._popups.forEach(p => this._updateCardStates(p));
      this._hideCartBanner();
      setTimeout(() => this._hide(), 500);

      const finalCart = await this._refreshCartItems();
      const nonGiftTotal = finalCart.items
        .filter(i => !this._hasGiftProperty(i))
        .reduce((sum, i) => sum + i.line_price, 0);
      this._injectBar(nonGiftTotal);

    } catch (err) {
      this.activeGiftVid = this._prevActiveVid;
      this.activeGiftTierIdx = -1;
      this._popups.forEach(p => this._updateCardStates(p));
    } finally {
      this.busy = false;
      this._setCartLoading(false);
      this._hidePopupOverlay();
      if (this._pendingCart) {
        const pending = this._pendingCart;
        this._pendingCart = null;
        this._pendingSync = false;
        await this._handleCart(pending);
      } else if (this._pendingSync) {
        this._pendingSync = false;
        await this._sync();
      }
    }
  }

  _watchBar() {
    if (this._isCartPage()) {
      const cartItems = document.querySelector('cart-items');
      if (cartItems) {
        let syncing = false;
        new MutationObserver(() => {
          if (syncing || this._lastTotal <= 0 || !this.activeGiftVid) return;
          if (this.busy) { this._pendingSync = true; return; }
          syncing = true;
          this._sync().finally(() => { syncing = false; });
        }).observe(cartItems, { childList: true, subtree: false });
      }
      return;
    }

    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    const reInject = () => {
      if (!drawer.hasAttribute('open') || this._lastTotal <= 0) return;
      if (!document.querySelector('.free-gift-bar')) {
        this._injectBar(this._lastTotal);
      }
    };
    new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'attributes') { reInject(); return; }
      }
      reInject();
    }).observe(drawer, { childList: true, subtree: false, attributes: true, attributeFilter: ['open'] });
  }

  _watch() {
    document.addEventListener('on:cart:add', (e) => {
      if (this.busy) { this._pendingCart = e.detail?.cart; return; }
      const cart = e.detail?.cart;
      if (cart) this._handleCart(cart);
      else this._sync();
    });

    document.addEventListener('cart:updated', () => {
      if (this.busy) { this._pendingSync = true; return; }
      this._sync();
    });

    document.addEventListener('on:line-item:change', (e) => {
      if (this.busy) { this._pendingSync = true; return; }
      document.querySelectorAll('.free-gift-cta-btn, .free-gift-change-btn').forEach(b => b.disabled = true);
      const cart = e.detail?.cart;
      if (cart) this._handleCart(cart);
      else this._sync();
    });

    document.addEventListener('on:cart-drawer:after-open', async () => {
      // .free-gift-bar persists across drawer close/open (it's only removed once the
      // total hits 0), so it's already showing the last-known state here — no need
      // to force a synchronous re-render before _sync(), which would just rebuild
      // the same DOM twice in a row and cause a visible flash.
      await this._sync();
      const currentTier = this._currentTier(this._lastTotal);
      if (this._ready && currentTier && !localStorage.getItem('giftPopupShown')) {
        localStorage.setItem('giftPopupShown', '1');
        this._show(false, currentTier);
      }
    });
  }

  _bindPopup() {
    document.addEventListener('click', async (e) => {
      if (e.target.closest('.free-gift-popup__close')) { this._hide(); return; }
      if (e.target.classList.contains('free-gift-popup')) { this._hide(); return; }

      const card = e.target.closest('.free-gift-popup__product');
      const btn = card ? card.querySelector('.free-gift-popup__atc') : e.target.closest('.free-gift-popup__atc');
      if (!btn || btn.disabled) return;

      const variantId = btn.dataset.variantId;
      if (!variantId) return;

      e.preventDefault();
      e.stopPropagation();

      if (btn.dataset.isRemove === 'true') {
        await this._removeActiveGiftAndClose();
        return;
      }

      this._prevActiveVid = this.activeGiftVid;
      this.activeGiftVid = String(variantId);
      this._popups.forEach(p => this._updateCardStates(p));

      this._showPopupOverlay();

      await this._add(variantId);
    });
  }

  _injectGiftLabel(variantId) {
    const cartItemsEl = document.querySelector('cart-items');
    if (!cartItemsEl) { return; }
    const vid = String(variantId);
    const byVid = [...cartItemsEl.querySelectorAll(`[data-variant-id="${vid}"]`)];
    const toRow = el => el.tagName === 'TR' ? el : (el.closest('tr.cart-item, li.cart-item, .cart-item, tr, li') || el);
    const all = [...new Set(byVid.map(toRow))].filter(Boolean);
    let row = all[0] || null;
    if (all.length > 1) {
      row = all.find(r => /\b0[,.]00\b/.test(r.querySelector('.price, .money, [class*="price"]')?.textContent || '')) || all[0];
    }
    if (!row) { return; }
    if (row.querySelector('.free-gift-cart-label')) { return; }
    const label = document.createElement('p');
    label.className = 'free-gift-cart-label';
    label.textContent = this._t('gift_offered_label');
    const priceEl = row.querySelector('.price__current, .cart-item__info .price, .cart-item__info [class*="price"]');
    if (priceEl) priceEl.insertAdjacentElement('afterend', label);
    else row.appendChild(label);
  }

  _showToast(message) {
    document.querySelectorAll('.free-gift-toast').forEach(t => t.remove());
    const toast = document.createElement('div');
    toast.className = 'free-gift-toast';
    toast.textContent = message;
    document.body.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    setTimeout(() => {
      toast.classList.remove('is-visible');
      setTimeout(() => toast.remove(), 350);
    }, 4000);
  }

  async _removeActiveGiftAndClose() {
    if (this.busy) return;
    this.busy = true;
    this._setCartLoading(true);
    this._showPopupOverlay();
    // Fade the row out immediately, before the network round-trip even starts —
    // otherwise the native cart-items line keeps showing its last-known state
    // (briefly a real, non-discounted price) until cart-items.refresh() catches
    // up with the removal. Same trick already used for the wrongGift auto-removal.
    if (this.activeGiftVid) {
      const giftEl = document.querySelector(`cart-items [data-variant-id="${this.activeGiftVid}"]`);
      const giftRow = giftEl?.closest('tr.cart-item, li.cart-item, .cart-item, tr, li') || giftEl;
      if (giftRow) {
        giftRow.style.transition = 'opacity 0.2s ease';
        giftRow.style.opacity = '0';
      }
    }
    try {
      const cart = await this._cart();
      const gifts = cart.items.filter(i => this._hasGiftProperty(i));
      for (const g of gifts) await this._removeGift(g.key);
      this.activeGiftVid = null;
      this.activeGiftKey = null;
      this.activeGiftTierIdx = -1;
      this._hide();
      const updatedCart = await this._refreshCartItems();
      const total = updatedCart.items
        .filter(i => !this._hasGiftProperty(i))
        .reduce((sum, i) => sum + i.line_price, 0);
      this._injectBar(total);
      this._showToast(this._t('toast_gift_removed'));
    } finally {
      this.busy = false;
      this._setCartLoading(false);
      this._hidePopupOverlay();
    }
  }

  _showPopupOverlay() {
    this._popups.forEach(p => {
      if (p.querySelector('.free-gift-popup__overlay')) return;
      const box = p.querySelector('.free-gift-popup__box');
      if (!box) return;
      const overlay = document.createElement('div');
      overlay.className = 'free-gift-popup__overlay';
      const spinner = document.createElement('div');
      spinner.className = 'free-gift-popup__overlay-spinner';
      overlay.appendChild(spinner);
      box.appendChild(overlay);
    });
  }

  _hidePopupOverlay() {
    document.querySelectorAll('.free-gift-popup__overlay').forEach(o => o.remove());
  }

  _showCartBanner(message) {
    document.querySelectorAll('.free-gift-banner').forEach(b => b.remove());
    const banner = document.createElement('div');
    banner.className = 'free-gift-banner';
    const textEl = document.createElement('span');
    textEl.className = 'free-gift-banner__text';
    textEl.appendChild(this._nodes(message));
    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'free-gift-banner__close';
    closeBtn.setAttribute('aria-label', this._t('close'));
    closeBtn.textContent = '×';
    closeBtn.addEventListener('click', () => banner.remove());
    banner.appendChild(textEl);
    banner.appendChild(closeBtn);
    if (this._isCartPage()) {
      const cartItemsEl = document.querySelector('cart-items');
      if (cartItemsEl) cartItemsEl.insertAdjacentElement('beforebegin', banner);
    } else {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || !drawer.hasAttribute('open')) return;
      const cartItemsEl = drawer.querySelector('cart-items');
      if (cartItemsEl) cartItemsEl.insertAdjacentElement('beforebegin', banner);
      else drawer.prepend(banner);
    }
    banner.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  _hideCartBanner() {
    document.querySelectorAll('.free-gift-banner').forEach(b => b.remove());
  }

}

  try { new FreeGiftPopup(); } catch (e) { console.error('[gift] init error:', e); }
})();
/* cache-bump 1790972522 */
